import React, {useCallback} from "react";
import {ISortBy} from "modules/types/team";
import {useDispatch, useSelector} from "react-redux";
import {
	checkIs3PlayerMaximumFromTheSameTeamSelector,
	getIsSelectedRoundNoActive,
	getIsTeamComplete,
	getPlayerById,
	getPlayerInTeamCheckSelector,
	getPositionsById,
	getSquadById,
	getTeamRemainingSalary,
	isTeamFullFilled,
} from "modules/selectors";
import styled from "styled-components";

import {PlayerPoolName, PlayerPoolStatus, PlayersPoolCell} from "./styled";
import {currencyFormatDecimal} from "modules/utils";
import {Exist, IconPlus, IconRemove, IconRollback, SquadLogo} from "components";
import {PureButton} from "components/styled";
import {
	pickPlayerToTeam,
	removePlayerFromTeam,
	tradeInPlayer,
	tradeOutPlayer,
} from "modules/actions";
import {openPlayerInfoModal} from "modules/actions/modals";
import {getIsTeamLockout} from "modules/selectors/lockout";
import {useIsPathIncludes} from "modules/utils/hooks";
import {useNavigate} from "react-router-dom";
import {ReactComponent as IconInfo} from "assets/images/icons/info_pool.svg";
import {IncompleteTeamActions} from "components/Team/PlayerPoll/IncompleteTeamActions";

const PlayerContainer = styled.div`
	display: flex;
	align-items: center;

	${PlayersPoolCell} {
		height: 45px;
		border-top: 1px solid #dddddd;
	}

	&.in-team {
		background: #ffffff;
	}
`;

const SquadLogoWrapper = styled.div`
	width: 32px;
	height: 24px;

	img {
		height: 100%;
		width: auto;
	}
`;

const PlayersPoolCellFirst = styled(PlayersPoolCell)`
	display: flex;
	align-items: center;
	justify-content: flex-start;
`;

const InfoButton = styled(PureButton)`
	&:disabled {
		cursor: initial;
	}
`;

interface IProps {
	readonly playerId: number;
	readonly sortBy: ISortBy;
	readonly style: object;
}

const getRowClassName = (isInTeam: boolean) => {
	return isInTeam ? "in-team" : "";
};

export const PlayersPollRow: React.FC<IProps> = ({playerId, sortBy, style}) => {
	const dispatch = useDispatch();
	const {preferredName, stats, cost, id, squadId, position} =
		useSelector(getPlayerById)(playerId);
	const squad = useSelector(getSquadById)(squadId);
	const positions = useSelector(getPositionsById);
	const isSelectedRoundNoActive = useSelector(getIsSelectedRoundNoActive);

	const getClassName = useCallback(
		(field: string) => {
			return sortBy.sortBy === field ? "active" : "";
		},
		[sortBy]
	);

	const showPlayerPopup = useCallback(() => {
		dispatch(openPlayerInfoModal(id));
	}, [id, dispatch]);

	return (
		<PlayerContainer className={getRowClassName(false)} style={style}>
			<PlayersPoolCellFirst className={"max-width"}>
				<PlayerPoolStatus className="pointer">
					<InfoButton
						// gtmId="player-pool-player-popup"
						disabled={isSelectedRoundNoActive}
						onClick={showPlayerPopup}>
						<IconInfo />
					</InfoButton>
				</PlayerPoolStatus>
				<PlayerPoolName onClick={showPlayerPopup} disabled={isSelectedRoundNoActive}>
					<p>
						<b>{preferredName}</b>
					</p>
					<p className="squad">
						{squad?.name}, {positions[position].shortName}
					</p>
				</PlayerPoolName>
			</PlayersPoolCellFirst>
			<PlayersPoolCell>
				<SquadLogoWrapper>
					<SquadLogo squadId={squadId} />
				</SquadLogoWrapper>
			</PlayersPoolCell>
			<PlayersPoolCell className={getClassName("cost")}>
				{currencyFormatDecimal(cost, true)}
			</PlayersPoolCell>
			<PlayersPoolCell className={getClassName("stats.total_points")}>
				{stats?.total_points ?? "-"}
			</PlayersPoolCell>
			<PlayersPoolCell className={getClassName("stats.picked_by")}>
				{stats?.picked_by ?? "-"}
			</PlayersPoolCell>

			<PlayerActions playerId={playerId} />
		</PlayerContainer>
	);
};

interface IButtonProps {
	color?: string;
}

const ActionButton = styled(PureButton)<IButtonProps>`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 45px;
	height: 45px;

	> div {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: ${(props) => props.color ?? "#000151"};
		display: flex;
		justify-content: center;
		align-items: center;
	}
`;

interface IPlayerActionsProps {
	playerId: number;
}

const PlayerActions: React.FC<IPlayerActionsProps> = ({playerId}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id, locked} = useSelector(getPlayerById)(playerId);
	const isTransferPage = useIsPathIncludes("transfers");
	const isSelectedRoundNoActive = useSelector(getIsSelectedRoundNoActive);

	const isInTeam = useSelector(getPlayerInTeamCheckSelector)(playerId);
	const isLockout = useSelector(getIsTeamLockout);

	const isTeamComplete = useSelector(getIsTeamComplete);

	const canTransferOutPlayer = isLockout && !locked;

	const removePlayer = useCallback(() => {
		dispatch(removePlayerFromTeam(id));
	}, [dispatch, id]);

	const transferPlayer = useCallback(() => {
		if (isTransferPage) {
			dispatch(removePlayerFromTeam(playerId));
			dispatch(tradeOutPlayer(playerId));
			return;
		}
		navigate(`transfers/${id}`);
	}, [dispatch, id, navigate, isTransferPage, playerId]);

	if (isSelectedRoundNoActive) {
		return <PlayersPoolCell />;
	}

	if (!isTeamComplete) {
		return (
			<PlayersPoolCell>
				<IncompleteTeamActions playerId={playerId} />
			</PlayersPoolCell>
		);
	}

	return (
		<PlayersPoolCell>
			<Exist when={!locked}>
				<Exist when={isInTeam}>
					<React.Fragment>
						<Exist when={!isLockout}>
							<ActionButton onClick={removePlayer} color="#F02F42">
								<div>
									<IconRemove width={8} height={8} color="#FFFFFF" />
								</div>
							</ActionButton>
						</Exist>
						<Exist when={canTransferOutPlayer}>
							<ActionButton onClick={transferPlayer}>
								<div>
									<IconRollback color="#FFFFFF" width={13} height={12} />
								</div>
							</ActionButton>
						</Exist>
					</React.Fragment>
				</Exist>
				<AddToTeamButton id={id} />
			</Exist>
		</PlayersPoolCell>
	);
};

interface IAddProps {
	id: number;
}

const AddToTeamButton: React.FC<IAddProps> = ({id}) => {
	const dispatch = useDispatch();
	const isCanBeAddedToTeam = !useSelector(isTeamFullFilled);
	const isInTeam = useSelector(getPlayerInTeamCheckSelector)(id);
	const Is3PlayerMaximumFromTheSameTeam = useSelector(
		checkIs3PlayerMaximumFromTheSameTeamSelector
	)(id);
	const isLockout = useSelector(getIsTeamLockout);
	const player = useSelector(getPlayerById)(id);
	const remainBudget = useSelector(getTeamRemainingSalary);

	const isAddVisible = !isInTeam && isCanBeAddedToTeam && !Is3PlayerMaximumFromTheSameTeam;

	const addPlayerToTeam = useCallback(() => {
		const action = isLockout ? tradeInPlayer : pickPlayerToTeam;
		dispatch(action(id));
	}, [dispatch, id, isLockout]);

	if (player.cost > remainBudget) {
		return null;
	}

	return (
		<Exist when={isAddVisible}>
			<ActionButton onClick={addPlayerToTeam}>
				<div>
					<IconPlus width={14} height={14} color="#FFFFFF" />
				</div>
			</ActionButton>
		</Exist>
	);
};
